/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { gql, type TypedDocumentNode } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import { zodResolver } from '@hookform/resolvers/zod'
import { ConfirmDialog } from '@repo/shared-client/components/blocks'
import { CheckBoxFormField } from '@repo/shared-client/components/fields'
import { Button, Card, CardContent, CardFooter, CardHeader, Spinner } from '@repo/shared-client/components/ui'
import { Form } from '@repo/shared-client/form'
import { createFileRoute, Link, redirect, useNavigate, useSearch } from '@tanstack/react-router'
import LogoFull from '@web/src/components/primatives/logo-full/logo-full'
import { authQueryKeys, useAuth } from '@web/src/hooks/use-auth'
import { useTranslation } from '@web/src/lib/intl/react'
import { userHasAcceptedRequiredLegalPolicies } from '@web/src/lib/legal/policy-version'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { BetterAuthSessionData, SessionUser } from '@web/src/lib/yoga/yoga-auth-context'
import { useState } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import type { AcceptLegalPoliciesMutation, AcceptLegalPoliciesMutationVariables } from 'types/graphql'
import { z } from 'zod'

const authInlineLegalLinkClass = 'inline font-semibold text-primary underline-offset-4 hover:underline'

function resolvePostAcceptPath(redirectTo: string | undefined): string {
  if (!redirectTo) {
    return '/'
  }
  if (!redirectTo.startsWith('/') || redirectTo.startsWith('//')) {
    return '/'
  }
  return redirectTo
}

export const ACCEPT_LEGAL_POLICIES: TypedDocumentNode<
  AcceptLegalPoliciesMutation,
  AcceptLegalPoliciesMutationVariables
> = gql`
  mutation AcceptLegalPolicies {
    acceptLegalPolicies
  }
`

export const Route = createFileRoute('/(public)/auth/accept-policies')({
  staticData: {
    disableSeasonalTheme: true,
  },
  component: AcceptPoliciesPage,
  validateSearch: z.object({
    redirectTo: z.string().optional(),
  }),
  beforeLoad: ({ context, search }) => {
    const session: BetterAuthSessionData = context.queryClient.getQueryData(authQueryKeys.session)
    const currentUser = session?.user as SessionUser

    if (!currentUser) {
      throw redirect({ href: '/auth/login?redirectTo=%2Fauth%2Faccept-policies' })
    }

    if (userHasAcceptedRequiredLegalPolicies(currentUser)) {
      throw redirect({ href: resolvePostAcceptPath(search.redirectTo) })
    }
  },
  head: () =>
    seo({
      title: 'Accept updated policies',
      description:
        'Review and accept the updated Terms of Use and Acceptable Use Policy to continue using Spicy Friends.',
      robots: robotsMetaForEnvironment({
        production: 'noindex, nofollow',
        staging: 'noindex,nofollow',
      }),
    }),
})

type FormValues = {
  acceptedLegalPolicies: boolean
}

function AcceptPoliciesPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { refreshSession } = useAuth()
  const { redirectTo } = useSearch({ from: '/(public)/auth/accept-policies' })
  const [isDeclining, setIsDeclining] = useState(false)
  const [isDeclineConfirmOpen, setIsDeclineConfirmOpen] = useState(false)

  const formMethods = useForm<FormValues>({
    resolver: zodResolver(
      z.object({
        acceptedLegalPolicies: z.boolean().refine((value) => value === true, {
          message: t('You must agree to continue'),
        }),
      })
    ),
    defaultValues: {
      acceptedLegalPolicies: false,
    },
  })

  const [acceptLegalPolicies, { loading: isAccepting }] = useMutation(ACCEPT_LEGAL_POLICIES)

  const onSubmit: SubmitHandler<FormValues> = async () => {
    try {
      await acceptLegalPolicies()
      await refreshSession()
      toast.success(t('Thank you for accepting the updated policies'))
      await navigate({ href: resolvePostAcceptPath(redirectTo) })
    } catch (err) {
      toast.error((err as Error)?.message ?? t('An error occurred'))
    }
  }

  const onDecline = async () => {
    setIsDeclining(true)
    try {
      await navigate({ to: '/auth/policies-declined' })
    } catch (err) {
      toast.error((err as Error)?.message ?? t('An error occurred'))
      setIsDeclining(false)
    }
  }

  const isBusy = isAccepting || isDeclining

  return (
    <div className="relative z-10 flex justify-center py-14">
      <Card className="mx-4 w-full max-w-lg shadow-2xl">
        <CardHeader className="flex flex-col items-center gap-2 text-center">
          <LogoFull size="md" />
          <h1 className="mt-2 font-semibold text-foreground text-lg">
            {t('Updated policies require your acceptance')}
          </h1>
        </CardHeader>

        <Form formMethods={formMethods} noValidate onSubmit={onSubmit}>
          <CardContent className="pb-4">
            <div className="flex flex-col gap-4">
              <p className="text-muted-foreground leading-relaxed">
                {t(
                  'We have updated our Terms of Use and Acceptable Use Policy. Please review and accept them to continue using Spicy Friends.'
                )}
              </p>

              <CheckBoxFormField name="acceptedLegalPolicies" required>
                {t('I have read and agree to the')}{' '}
                <Link
                  className={authInlineLegalLinkClass}
                  rel="noopener noreferrer"
                  target="_blank"
                  to="/helpcenter/legal/terms-of-use"
                >
                  {t('Terms of Use')}
                </Link>{' '}
                {t('and')}{' '}
                <Link
                  className={authInlineLegalLinkClass}
                  rel="noopener noreferrer"
                  target="_blank"
                  to="/helpcenter/legal/acceptable-use-policy"
                >
                  {t('Acceptable Use Policy')}
                </Link>
              </CheckBoxFormField>
            </div>
          </CardContent>
          <CardFooter className="gap-3">
            <Button
              className="flex-1"
              disabled={isBusy}
              onClick={() => setIsDeclineConfirmOpen(true)}
              size="lg"
              type="button"
              variant="outline"
            >
              {t('Decline')}
            </Button>
            <Button className="flex-1" disabled={isBusy} size="lg" type="submit">
              {isAccepting ? <Spinner className="mr-2" /> : null}
              {t('Accept and continue')}
            </Button>
          </CardFooter>
        </Form>
      </Card>

      <ConfirmDialog
        cancelText={t('Go back')}
        confirmText={t('Yes, decline')}
        loading={isDeclining}
        onConfirm={onDecline}
        onOpenChange={(details) => {
          if (!isDeclining) {
            setIsDeclineConfirmOpen(details.open)
          }
        }}
        open={isDeclineConfirmOpen}
        text={t(
          'If you decline the updated Terms of Use and Acceptable Use Policy, you will no longer be able to use the site. Are you sure this is what you want to do?'
        )}
        title={t('Decline updated policies?')}
        variant="basic"
      />
    </div>
  )
}
